import { Box, Button, Typography, TextField, Switch, FormControlLabel, Card, CardContent, Grid, Alert, Divider, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Paper, Chip, CircularProgress } from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import Skeleton from "react-loading-skeleton";
import { collection, getDocs, doc, setDoc, updateDoc, Timestamp, onSnapshot, limit, query, where } from "firebase/firestore";
import { DATABASE } from "../../../Config/firebase";
import { toast } from "react-toastify";
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupsIcon from '@mui/icons-material/Groups';
import { computeAdoptionStats, ADOPTION_STATUS, ADOPTION_STATUS_META } from "../../../Utils/appVersionAdoptionUtils";

/** App Version Adoption (+35): linha de métricas para um grupo (All/Individual/Corporate). */
const AdoptionGroup = ({ title, bucket }) => {
  if (!bucket) return null;
  const statuses = [
    ADOPTION_STATUS.UP_TO_DATE,
    ADOPTION_STATUS.OUTDATED,
    ADOPTION_STATUS.UNKNOWN,
  ];
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="body1" sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#fff' }}>
          {title}
        </Typography>
        <Chip
          label={`${bucket.total} total`}
          size="small"
          sx={{ backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600 }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {statuses.map((status) => {
          const meta = ADOPTION_STATUS_META[status];
          return (
            <Chip
              key={status}
              label={`${meta.label}: ${bucket[status]}`}
              size="small"
              sx={{
                backgroundColor: `${meta.color}22`,
                color: meta.color,
                border: `1px solid ${meta.color}`,
                fontWeight: 600,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const AppVersionSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingMaintenanceMode, setPendingMaintenanceMode] = useState(false);

  // App Version Adoption (+35): métricas de quem já atualizou.
  const [adoptionStats, setAdoptionStats] = useState(null);
  const [adoptionLoading, setAdoptionLoading] = useState(false);
  const [adoptionLoadedAt, setAdoptionLoadedAt] = useState(null);
  
  useEffect(() => {
    const fetchAppVersion = async () => {
      try {
        setIsLoading(true);
        const appVersionQuery = query(
          collection(DATABASE, "app_version"),
          limit(1)
        );
        const querySnapshot = await getDocs(appVersionQuery);
        
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          setData({
            id: querySnapshot.docs[0].id,
            ...docData
          });
        } else {
          // Set default values if document doesn't exist
          setData({
            title: "",
            subtitle: "",
            app_store_url: "",
            play_store_url: "",
            force_update: false,
            force_update_android: false,
            force_update_ios: false,
            android_build_number: 0,
            ios_build_number: 0,
            maintenance_mode: false,
            maintenance_message: "System is under maintenance. Please access app.fiddux.com to continue using the service.",
            maintenance_redirect_url: "https://app.fiddux.com"
          });
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching app version: ", error);
        toast.error("Failed to load app version settings.");
        setIsLoading(false);
      }
    };

    fetchAppVersion();
  }, []);

  // App Version Adoption (+35): busca clientes (role === 1) sob demanda e calcula
  // up to date / outdated / unknown. Usa getDocs (sem onSnapshot permanente).
  const loadAdoption = useCallback(async () => {
    try {
      setAdoptionLoading(true);
      const targetIos = parseInt(data?.ios_build_number, 10) || 0;
      const targetAndroid = parseInt(data?.android_build_number, 10) || 0;

      const usersQuery = query(collection(DATABASE, "users"), where("role", "==", 1));
      const snap = await getDocs(usersQuery);
      const users = snap.docs.map((d) => d.data());

      setAdoptionStats(computeAdoptionStats(users, targetIos, targetAndroid));
      setAdoptionLoadedAt(new Date());
    } catch (error) {
      console.error("Error loading adoption stats: ", error);
      toast.error("Failed to load adoption stats.");
    } finally {
      setAdoptionLoading(false);
    }
  }, [data?.ios_build_number, data?.android_build_number]);

  // Carrega uma vez quando os dados de versão estiverem prontos.
  useEffect(() => {
    if (data && !adoptionStats && !adoptionLoading) {
      loadAdoption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: data?.title || "",
      subtitle: data?.subtitle || "",
      app_store_url: data?.app_store_url || "",
      play_store_url: data?.play_store_url || "",
      force_update: data?.force_update || false,
      force_update_android: data?.force_update_android ?? data?.force_update ?? false,
      force_update_ios: data?.force_update_ios ?? data?.force_update ?? false,
      android_build_number: data?.android_build_number || 0,
      ios_build_number: data?.ios_build_number || 0,
      maintenance_mode: data?.maintenance_mode || false,
      maintenance_message: data?.maintenance_message || "System is under maintenance. Please access app.fiddux.com to continue using the service.",
      maintenance_redirect_url: data?.maintenance_redirect_url || "https://app.fiddux.com"
    },
    validationSchema: Yup.object({
      maintenance_message: Yup.string().required("Maintenance message is required"),
      maintenance_redirect_url: Yup.string().url("Must be a valid URL").required("Redirect URL is required"),
    }),
    onSubmit: async (values) => {
      setIsUpdating(true);
      try {
        let docRef;
        if (data?.id) {
          docRef = doc(DATABASE, "app_version", data.id);
          await updateDoc(docRef, {
            title: values.title,
            subtitle: values.subtitle,
            app_store_url: values.app_store_url,
            play_store_url: values.play_store_url,
            force_update: values.force_update,
            force_update_android: values.force_update_android,
            force_update_ios: values.force_update_ios,
            android_build_number: parseInt(values.android_build_number) || 0,
            ios_build_number: parseInt(values.ios_build_number) || 0,
            maintenance_mode: values.maintenance_mode,
            maintenance_message: values.maintenance_message,
            maintenance_redirect_url: values.maintenance_redirect_url,
            update_date: Timestamp.now(),
          });
        } else {
          // Create new document - get first available doc ID
          const appVersionQuery = query(
            collection(DATABASE, "app_version"),
            limit(1)
          );
          const querySnapshot = await getDocs(appVersionQuery);
          const docId = querySnapshot.empty ? "version" : querySnapshot.docs[0].id;
          
          docRef = doc(DATABASE, "app_version", docId);
          await setDoc(docRef, {
            title: values.title,
            subtitle: values.subtitle,
            app_store_url: values.app_store_url,
            play_store_url: values.play_store_url,
            force_update: values.force_update,
            force_update_android: values.force_update_android,
            force_update_ios: values.force_update_ios,
            android_build_number: parseInt(values.android_build_number) || 0,
            ios_build_number: parseInt(values.ios_build_number) || 0,
            maintenance_mode: values.maintenance_mode,
            maintenance_message: values.maintenance_message,
            maintenance_redirect_url: values.maintenance_redirect_url,
            create_date: Timestamp.now(),
            update_date: Timestamp.now(),
          });
        }
        toast.success("App version settings updated successfully!");
        setIsUpdating(false);
      } catch (error) {
        console.error("Error updating app version: ", error);
        toast.error("Failed to update app version settings.");
        setIsUpdating(false);
      }
    },
  });

  const handleMaintenanceModeToggle = (newValue) => {
    if (newValue && !formik.values.maintenance_mode) {
      // Turning ON - show confirmation dialog
      setPendingMaintenanceMode(true);
      setConfirmDialogOpen(true);
    } else {
      // Turning OFF - allow directly
      formik.setFieldValue('maintenance_mode', newValue);
    }
  };

  const handleConfirmMaintenanceMode = () => {
    formik.setFieldValue('maintenance_mode', true);
    setConfirmDialogOpen(false);
    setPendingMaintenanceMode(false);
    toast.warning("Maintenance mode has been ACTIVATED. Users will see the maintenance screen.");
  };

  const handleCancelMaintenanceMode = () => {
    setConfirmDialogOpen(false);
    setPendingMaintenanceMode(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={400} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Purple Gradient */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
          borderRadius: '16px',
          p: 3,
          mb: 3,
          color: '#fff',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PhoneAndroidIcon sx={{ fontSize: 48, color: '#fff' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#fff' }}>
              App Version Settings
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, color: '#fff' }}>
              Manage app version information, force update settings, and maintenance mode
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Critical Warning Alert */}
      <Alert 
        severity="warning" 
        icon={<WarningIcon sx={{ fontSize: 28 }} />}
        sx={{ 
          mb: 4,
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          border: '2px solid #ff9800',
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#ff9800', mb: 1 }}>
          ⚠️ Important: Maintenance Mode Impact
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1rem', color: '#fff', mb: 1.5, lineHeight: 1.6 }}>
          <strong>When Maintenance Mode is activated:</strong>
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 1.5, color: '#fff' }}>
          <li style={{ marginBottom: '8px', fontSize: '1rem' }}>
            <strong>All mobile app users</strong> will see a maintenance screen instead of the app
          </li>
          <li style={{ marginBottom: '8px', fontSize: '1rem' }}>
            Users will be <strong>blocked from accessing</strong> their accounts via the mobile app
          </li>
          <li style={{ marginBottom: '8px', fontSize: '1rem' }}>
            Users must access the service via <strong>app.fiddux.com</strong> (web version)
          </li>
          <li style={{ marginBottom: '8px', fontSize: '1rem' }}>
            This should only be used during <strong>critical updates, migrations, or emergencies</strong>
          </li>
        </Box>
        <Typography variant="body2" sx={{ fontSize: '0.95rem', color: '#ffd54f', fontStyle: 'italic', mt: 1 }}>
          ⚡ Make sure app.fiddux.com is configured and accessible before activating maintenance mode.
        </Typography>
      </Alert>

      {formik.values.maintenance_mode && (
        <Alert 
          severity="error" 
          icon={<WarningIcon sx={{ fontSize: 28 }} />}
          sx={{ 
            mb: 4,
            backgroundColor: 'rgba(245, 42, 45, 0.2)',
            border: '2px solid #f52a2d',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <WarningIcon sx={{ mr: 1, fontSize: 28, color: '#f52a2d' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#f52a2d' }}>
              🚨 Maintenance Mode is CURRENTLY ACTIVE
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mt: 1.5, fontSize: '1rem', color: '#fff', lineHeight: 1.6 }}>
            <strong>Status:</strong> All users opening the mobile app will see the maintenance screen. 
            They will be redirected to <strong>{formik.values.maintenance_redirect_url || 'app.fiddux.com'}</strong> to access the service.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5, fontSize: '0.95rem', color: '#ffcdd2', fontStyle: 'italic' }}>
            Turn off maintenance mode when the issue is resolved to restore normal app access.
          </Typography>
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Maintenance Mode Section */}
          <Grid item xs={12}>
            <Card sx={{ 
              background: formik.values.maintenance_mode 
                ? "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)"
                : "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
              border: formik.values.maintenance_mode 
                ? '2px solid #ff9800' 
                : '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: formik.values.maintenance_mode 
                ? "0 4px 12px rgba(255, 152, 0, 0.3)"
                : "0 2px 8px rgba(0, 0, 0, 0.3)",
            }}>
              <CardContent sx={{ color: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BuildIcon sx={{ fontSize: 32, mr: 1.5, color: formik.values.maintenance_mode ? '#ff9800' : '#2af598' }} />
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: '1.5rem', color: '#fff' }}>
                    Maintenance Mode
                  </Typography>
                </Box>
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  backgroundColor: 'rgba(42, 245, 152, 0.1)',
                  borderLeft: '4px solid #2af598',
                  borderRadius: 1,
                }}>
                  <Typography variant="body1" sx={{ color: "#fff", fontSize: '1rem', lineHeight: 1.6 }}>
                    <strong style={{ color: '#2af598' }}>Purpose:</strong> Temporarily disable mobile app access and redirect users to the web version. 
                    This is useful during critical updates, migrations, or when issues are detected. When enabled, users opening 
                    the app will see a maintenance message and a button to access the web version instead.
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  mb: 3, 
                  p: 2.5, 
                  backgroundColor: formik.values.maintenance_mode 
                    ? 'rgba(255, 152, 0, 0.15)' 
                    : 'rgba(255, 255, 255, 0.05)',
                  border: formik.values.maintenance_mode 
                    ? '2px solid #ff9800' 
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.maintenance_mode}
                        onChange={(e) => handleMaintenanceModeToggle(e.target.checked)}
                        color="warning"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#ff9800',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#ff9800',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ 
                        fontWeight: formik.values.maintenance_mode ? 'bold' : 'normal', 
                        fontSize: '1.2rem',
                        color: formik.values.maintenance_mode ? '#ff9800' : '#fff',
                      }}>
                        {formik.values.maintenance_mode ? '🔴 Maintenance Mode: ON' : '⚪ Maintenance Mode: OFF'}
                      </Typography>
                    }
                  />
                </Box>

                <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <InfoIcon sx={{ fontSize: 20, mr: 1, color: '#2af598' }} />
                    <Typography variant="body1" sx={{ mb: 0, fontSize: '1.1rem', fontWeight: '600', color: '#fff' }}>
                      Maintenance Message
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: '0.95rem', color: "#aaa", fontStyle: 'italic', lineHeight: 1.6 }}>
                    The message displayed to users when they open the app during maintenance. Use clear, professional language 
                    that matches your brand voice. The message appears below the logo and should explain why maintenance is happening 
                    and how to access the service via the web.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    id="maintenance_message"
                    name="maintenance_message"
                    label="Enter maintenance message"
                    value={formik.values.maintenance_message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.maintenance_message && Boolean(formik.errors.maintenance_message)}
                    helperText={
                      formik.touched.maintenance_message && formik.errors.maintenance_message
                        ? formik.errors.maintenance_message
                        : "This message will be displayed to users when maintenance mode is active."
                    }
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '1rem',
                        color: '#fff',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '1rem',
                        color: '#aaa',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#2af598',
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#2af598',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2af598',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#aaa',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <InfoIcon sx={{ fontSize: 20, mr: 1, color: '#2af598' }} />
                    <Typography variant="body1" sx={{ mb: 0, fontSize: '1.1rem', fontWeight: '600', color: '#fff' }}>
                      Redirect URL
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: '0.95rem', color: "#aaa", fontStyle: 'italic', lineHeight: 1.6 }}>
                    The web URL where users will be redirected when they click "Access via Browser" on the maintenance screen. 
                    This should point to your web app (e.g., https://app.fiddux.com) which should be configured to use the old 
                    production database during maintenance periods.
                  </Typography>
                  <TextField
                    fullWidth
                    id="maintenance_redirect_url"
                    name="maintenance_redirect_url"
                    label="Enter redirect URL"
                    value={formik.values.maintenance_redirect_url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.maintenance_redirect_url && Boolean(formik.errors.maintenance_redirect_url)}
                    helperText={
                      formik.touched.maintenance_redirect_url && formik.errors.maintenance_redirect_url
                        ? formik.errors.maintenance_redirect_url
                        : "Must be a valid URL (e.g., https://app.fiddux.com)"
                    }
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '1rem',
                        color: '#fff',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '1rem',
                        color: '#aaa',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#2af598',
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#2af598',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2af598',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#aaa',
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
          </Grid>

          {/* App Version Info Section */}
          <Grid item xs={12}>
            <Card sx={{ 
              background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
              border: "1px solid rgba(245, 42, 45, 0.2)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
            }}>
              <CardContent sx={{ color: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 28, mr: 1.5, color: '#f52a2d' }} />
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: '1.5rem', color: '#fff' }}>
                    App Version Information
                  </Typography>
                </Box>
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  backgroundColor: 'rgba(245, 42, 45, 0.1)',
                  borderLeft: '4px solid #f52a2d',
                  borderRadius: 1,
                }}>
                  <Typography variant="body1" sx={{ color: "#fff", fontSize: '1rem', lineHeight: 1.6 }}>
                    Configure app version details displayed in app stores and manage force update settings. These settings control 
                    how the app behaves when users have outdated versions installed.
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: '500', color: '#fff' }}>
                      Title
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem', color: "#aaa", fontStyle: 'italic' }}>
                      App title displayed in app stores (iOS App Store and Google Play Store).
                    </Typography>
                    <TextField
                      fullWidth
                      id="title"
                      name="title"
                      label="Enter app title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: '#fff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem',
                          color: '#aaa',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#2af598',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2af598',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2af598',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: '500', color: '#fff' }}>
                      Title
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem', color: "#aaa", fontStyle: 'italic' }}>
                      App title displayed in app stores (iOS App Store and Google Play Store).
                    </Typography>
                    <TextField
                      fullWidth
                      id="title"
                      name="title"
                      label="Enter app title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: '#fff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem',
                          color: '#aaa',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#2af598',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2af598',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2af598',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: '500', color: '#fff' }}>
                      Subtitle
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem', color: "#aaa", fontStyle: 'italic' }}>
                      Short description or tagline shown below the title in app stores.
                    </Typography>
                    <TextField
                      fullWidth
                      id="subtitle"
                      name="subtitle"
                      label="Enter subtitle"
                      value={formik.values.subtitle}
                      onChange={formik.handleChange}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: '#fff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem',
                          color: '#aaa',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#2af598',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2af598',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2af598',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: '500', color: '#fff' }}>
                      App Store URL
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem', color: "#aaa", fontStyle: 'italic' }}>
                      Direct link to the app in the iOS App Store. Used for force update redirects.
                    </Typography>
                    <TextField
                      fullWidth
                      id="app_store_url"
                      name="app_store_url"
                      label="Enter iOS App Store URL"
                      value={formik.values.app_store_url}
                      onChange={formik.handleChange}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: '#fff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem',
                          color: '#aaa',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#2af598',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2af598',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2af598',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: '500', color: '#fff' }}>
                      Play Store URL
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem', color: "#aaa", fontStyle: 'italic' }}>
                      Direct link to the app in Google Play Store. Used for force update redirects.
                    </Typography>
                    <TextField
                      fullWidth
                      id="play_store_url"
                      name="play_store_url"
                      label="Enter Google Play Store URL"
                      value={formik.values.play_store_url}
                      onChange={formik.handleChange}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: '#fff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem',
                          color: '#aaa',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#2af598',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2af598',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2af598',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: '500', color: '#fff' }}>
                      Android Build Number
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem', color: "#aaa", fontStyle: 'italic' }}>
                      Current Android app build number. Used to determine if users need to update their app.
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      id="android_build_number"
                      name="android_build_number"
                      label="Enter Android build number"
                      value={formik.values.android_build_number}
                      onChange={formik.handleChange}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: '#fff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem',
                          color: '#aaa',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#2af598',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2af598',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2af598',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.95rem', fontWeight: '500', color: '#fff' }}>
                      iOS Build Number
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem', color: "#aaa", fontStyle: 'italic' }}>
                      Current iOS app build number. Used to determine if users need to update their app.
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      id="ios_build_number"
                      name="ios_build_number"
                      label="Enter iOS build number"
                      value={formik.values.ios_build_number}
                      onChange={formik.handleChange}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          color: '#fff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '1rem',
                          color: '#aaa',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#2af598',
                        },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2af598',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2af598',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'rgba(245, 42, 45, 0.1)',
                      borderRadius: 2,
                      border: '1px solid rgba(245, 42, 45, 0.3)',
                    }}>
                      <Typography variant="body1" sx={{ mb: 1, fontSize: '1.1rem', fontWeight: '600', color: '#fff' }}>
                        Force Update
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, fontSize: '0.95rem', color: "#aaa", fontStyle: 'italic', lineHeight: 1.6 }}>
                        Apps on build 37+ read the iOS / Android switches below. Apps still on 35/36 only read the legacy switch.
                        Keep legacy ON until Adoption shows everyone on 37, otherwise an old install will not be forced.
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.force_update_ios}
                              onChange={(e) => formik.setFieldValue('force_update_ios', e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#f52a2d',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#f52a2d',
                                },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIphoneIcon sx={{ fontSize: 20, color: formik.values.force_update_ios ? '#f52a2d' : '#fff' }} />
                              <Typography variant="body1" sx={{
                                fontSize: '1.1rem',
                                fontWeight: formik.values.force_update_ios ? 'bold' : 'normal',
                                color: formik.values.force_update_ios ? '#f52a2d' : '#fff',
                              }}>
                                {formik.values.force_update_ios ? 'Force Update iOS: ENABLED' : 'Force Update iOS: DISABLED'}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.force_update_android}
                              onChange={(e) => formik.setFieldValue('force_update_android', e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#f52a2d',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#f52a2d',
                                },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneAndroidIcon sx={{ fontSize: 20, color: formik.values.force_update_android ? '#f52a2d' : '#fff' }} />
                              <Typography variant="body1" sx={{
                                fontSize: '1.1rem',
                                fontWeight: formik.values.force_update_android ? 'bold' : 'normal',
                                color: formik.values.force_update_android ? '#f52a2d' : '#fff',
                              }}>
                                {formik.values.force_update_android ? 'Force Update Android: ENABLED' : 'Force Update Android: DISABLED'}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.force_update}
                              onChange={(e) => formik.setFieldValue('force_update', e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#f52a2d',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#f52a2d',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body1" sx={{ 
                              fontSize: '1.1rem', 
                              fontWeight: formik.values.force_update ? 'bold' : 'normal',
                              color: formik.values.force_update ? '#f52a2d' : '#fff',
                            }}>
                              {formik.values.force_update
                                ? 'Legacy (both platforms): ENABLED'
                                : 'Legacy (both platforms): DISABLED'}
                            </Typography>
                          }
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#aaa', fontStyle: 'italic', pl: 1 }}>
                          Legacy — both platforms; only apps before build 37 read this.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* App Version Adoption / Rollout (+35) */}
          <Grid item xs={12}>
            <Card sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }}>
              <CardContent sx={{ color: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupsIcon sx={{ fontSize: 28, mr: 1.5, color: '#fff' }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#fff' }}>
                      App Version Adoption
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={adoptionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                    onClick={loadAdoption}
                    disabled={adoptionLoading}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      color: '#fff',
                      '&:hover': { borderColor: '#2af598', backgroundColor: 'rgba(42, 245, 152, 0.1)' },
                    }}
                  >
                    {adoptionLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </Box>

                <Box sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  borderLeft: '4px solid #2af598',
                  borderRadius: 1,
                }}>
                  <Typography variant="body1" sx={{ color: '#fff', fontSize: '1rem', lineHeight: 1.6 }}>
                    How many clients have updated, split by Individual and Corporate. Up to date means
                    the reported build is greater than or equal to the target build for that platform
                    (iOS: <strong>{parseInt(data?.ios_build_number, 10) || 0}</strong>, Android: <strong>{parseInt(data?.android_build_number, 10) || 0}</strong>).
                    Unknown means the client has not reported a version yet.
                  </Typography>
                  {adoptionLoadedAt && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontStyle: 'italic', mt: 1 }}>
                      Last refreshed: {adoptionLoadedAt.toLocaleString()}
                    </Typography>
                  )}
                </Box>

                {adoptionLoading && !adoptionStats ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: '#fff' }} />
                  </Box>
                ) : adoptionStats ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <AdoptionGroup title="All clients" bucket={adoptionStats.all} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <AdoptionGroup title="Individual" bucket={adoptionStats.individual} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <AdoptionGroup title="Corporate" bucket={adoptionStats.corporate} />
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
                    No data loaded yet. Click Refresh to compute adoption.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => formik.resetForm()}
                disabled={isUpdating}
                sx={{
                  fontSize: '1rem',
                  padding: '10px 24px',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: '#fff',
                  '&:hover': {
                    borderColor: '#2af598',
                    backgroundColor: 'rgba(42, 245, 152, 0.1)',
                  },
                }}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isUpdating}
                sx={{
                  background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  fontSize: '1rem',
                  padding: '10px 24px',
                  '&:hover': {
                    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                    opacity: 0.9,
                  },
                }}
              >
                {isUpdating ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Confirmation Dialog for Maintenance Mode */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelMaintenanceMode}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: '2px solid #ff9800',
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 'bold', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ fontSize: 32, mr: 1.5, color: '#ff9800' }} />
            Confirm Maintenance Mode Activation
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#fff', fontSize: '1rem', lineHeight: 1.6, mb: 2 }}>
            <strong style={{ color: '#ff9800' }}>⚠️ WARNING: This action will immediately affect all mobile app users.</strong>
          </DialogContentText>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'rgba(255, 152, 0, 0.15)',
            borderRadius: 2,
            border: '1px solid #ff9800',
            mb: 2,
          }}>
            <Typography variant="body1" sx={{ color: '#fff', fontSize: '1rem', mb: 1.5, fontWeight: 'bold' }}>
              What will happen:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 0, color: '#fff' }}>
              <li style={{ marginBottom: '8px', fontSize: '0.95rem' }}>
                <strong>All users</strong> opening the mobile app will see a maintenance screen
              </li>
              <li style={{ marginBottom: '8px', fontSize: '0.95rem' }}>
                Users will be <strong>blocked from accessing</strong> their accounts via the app
              </li>
              <li style={{ marginBottom: '8px', fontSize: '0.95rem' }}>
                Users must access the service via <strong>app.fiddux.com</strong> (web version)
              </li>
              <li style={{ marginBottom: '8px', fontSize: '0.95rem' }}>
                This should only be used during <strong>critical situations</strong>
              </li>
            </Box>
          </Box>
          <DialogContentText sx={{ color: '#aaa', fontSize: '0.95rem', fontStyle: 'italic' }}>
            Make sure app.fiddux.com is configured and accessible before confirming. 
            You can turn off maintenance mode at any time to restore normal app access.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={handleCancelMaintenanceMode}
            sx={{
              color: '#aaa',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMaintenanceMode}
            variant="contained"
            sx={{
              backgroundColor: '#ff9800',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#f57c00',
              },
            }}
          >
            Yes, Activate Maintenance Mode
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppVersionSettings;
